/**
 * The data layer for a specification matter's goods and services —
 * `GET`/`PUT /api/matters/{id}/classes`.
 *
 * Its own module for the reason `opposition.ts` gives for being one: a module
 * holds one feature area. Specification work has a per-class table, a proposed
 * wording distinct from the current one, and a provenance question none of the
 * P2 control screens know or should learn.
 *
 * Three rules, each because breaking it has a specific failure:
 *
 * - **A missing proposal is `null`, never `''`.** "Nobody has amended this
 *   class yet" and "this class is amended to nothing" are different answers and
 *   only one of them is a filing. The column is nullable for that reason and
 *   this layer must not flatten it — a screen that renders both as an empty
 *   input invites the attorney to file the second by accident.
 * - **`nice_source_id` is the provenance and is never inferred.** A row backed
 *   by a retrieved description carries its id; a row that is not carries
 *   `null`. Deriving "sourced" from anything else here — the wording matching,
 *   the field being non-empty — would be a second opinion about provenance,
 *   and the gate's `SPEC_WORDING_UNANCHORED` is the first.
 * - **The write is the whole set.** {@link useSaveClasses} sends every class,
 *   because an identification of goods and services is one document: a class
 *   surrendered and a class narrowed are a single decision. Per-row saves
 *   would let a browser that died between two of them leave a specification
 *   nobody chose.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

import { api } from './client'

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** `MatterClassResponse`. One class of the application. */
export interface MatterClass {
  class_number: number
  /** What the application says today. Never empty — the server requires it. */
  current_text: string
  /** What the response will ask for, or `null` when nothing has proposed yet. */
  proposed_text: string | null
  /** The `tm_nice_class` row a proposal came from, or `null` when none did. */
  nice_source_id: number | null
}

/** `MatterClassInput`. One row as the attorney states it. */
export interface MatterClassInput {
  class_number: number
  current_text: string
  proposed_text?: string | null
  nice_source_id?: number | null
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

export const specificationKeys = {
  classes: (matterId: string) => ['matters', matterId, 'classes'] as const,
}

export function useMatterClasses(matterId: string): UseQueryResult<MatterClass[]> {
  return useQuery<MatterClass[]>({
    queryKey: specificationKeys.classes(matterId),
    queryFn: () => api.get<MatterClass[]>(`/api/matters/${encodeURIComponent(matterId)}/classes`),
  })
}

export function useSaveClasses(matterId: string) {
  const client = useQueryClient()
  return useMutation<MatterClass[], Error, MatterClassInput[]>({
    mutationFn: (classes) =>
      api.put<MatterClass[]>(`/api/matters/${encodeURIComponent(matterId)}/classes`, { classes }),
    onSuccess: (rows) => {
      // Seed the cache from the server's answer rather than invalidating: the
      // response IS the new set, and a refetch would show the old one for a
      // frame on a slow link — on a table the attorney just edited.
      client.setQueryData(specificationKeys.classes(matterId), rows)
    },
  })
}

// ---------------------------------------------------------------------------
// What the screen needs to know about a row
// ---------------------------------------------------------------------------

/**
 * How a row's proposed wording is backed, for display.
 *
 * Three states and not two: a class nobody has amended is not the same as one
 * amended with nothing behind it, and collapsing them would put a warning on
 * every untouched class of every application.
 */
export type ProposalSource = 'unamended' | 'sourced' | 'unsourced'

export function proposalSource(row: MatterClass): ProposalSource {
  if (row.proposed_text === null || row.proposed_text.trim() === '') return 'unamended'
  return row.nice_source_id === null ? 'unsourced' : 'sourced'
}

/** Copy for each state. The wording is deliberate — see the module docstring. */
export const SOURCE_LABEL: Record<ProposalSource, string> = {
  unamended: 'not amended',
  sourced: 'from a pre-approved description',
  unsourced: 'no pre-approved source',
}

/**
 * Never "the office will accept this". The Nice Classification is
 * international and each office keeps its own list of what it will take, so a
 * sourced row means the wording is real and quotable, not that it is safe.
 */
export const SOURCED_CAVEAT =
  'Pre-approved under the Nice Classification. Each office keeps its own list — check before filing.'
